import {
  DEFAULT_TARGETS,
  type Cell,
  type GeneratorOptions,
  type GeneratorTargets,
  type MapDocument,
  type TerrainId,
} from './types';
import { cellIndex, clamp, neighbours } from './iso';

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = seed + 0x6d2b79f5 | 0;
    let value = Math.imul(seed ^ seed >>> 15, 1 | seed);
    value = value + Math.imul(value ^ value >>> 7, 61 | value) ^ value;
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  };
}

function coordinateRandom(seed: number, x: number, y: number): number {
  let value = seed ^ Math.imul(x + 374761393, 668265263) ^ Math.imul(y + 1274126177, 2246822519);
  value = Math.imul(value ^ value >>> 13, 1274126177);
  return ((value ^ value >>> 16) >>> 0) / 4294967295;
}

function smoothStep(value: number): number {
  return value * value * (3 - 2 * value);
}

function valueNoise(seed: number, x: number, y: number, scale: number): number {
  const sx = x / scale;
  const sy = y / scale;
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const tx = smoothStep(sx - x0);
  const ty = smoothStep(sy - y0);
  const n00 = coordinateRandom(seed, x0, y0);
  const n10 = coordinateRandom(seed, x0 + 1, y0);
  const n01 = coordinateRandom(seed, x0, y0 + 1);
  const n11 = coordinateRandom(seed, x0 + 1, y0 + 1);
  const top = n00 + (n10 - n00) * tx;
  const bottom = n01 + (n11 - n01) * tx;
  return top + (bottom - top) * ty;
}

function fractalNoise(seed: number, x: number, y: number, baseScale: number): number {
  return valueNoise(seed, x, y, baseScale) * 0.56
    + valueNoise(seed + 1013, x, y, baseScale / 2) * 0.28
    + valueNoise(seed + 7919, x, y, baseScale / 4) * 0.16;
}

function normalizeTargets(targets: Partial<GeneratorTargets>): GeneratorTargets {
  const merged = { ...DEFAULT_TARGETS, ...targets };
  const sum = Object.values(merged).reduce((total, value) => total + Math.max(0, Number(value)), 0) || 100;
  const normalized = {} as GeneratorTargets;
  for (const [id, value] of Object.entries(merged) as Array<[TerrainId, number]>) {
    normalized[id] = Math.max(0, Number(value)) / sum * 100;
  }
  return normalized;
}

function takeByScore(
  candidates: number[],
  desired: number,
  score: (index: number) => number,
  assigned: Array<TerrainId | null>,
  terrain: TerrainId,
): void {
  candidates
    .filter((index) => assigned[index] === null)
    .sort((a, b) => score(b) - score(a) || a - b)
    .slice(0, Math.max(0, desired))
    .forEach((index) => { assigned[index] = terrain; });
}

export function generateMap(
  title: string,
  width: number,
  height: number,
  options: Partial<GeneratorOptions> = {},
  stylePackVersion = 1,
): MapDocument {
  const seed = String(options.seed || `${Date.now()}`);
  const numericSeed = hashString(seed);
  const random = mulberry32(numericSeed);
  const targets = normalizeTargets(options.targets ?? DEFAULT_TARGETS);
  const heightIntensity = clamp(Number(options.heightIntensity ?? 0.72), 0, 1);
  const landform = options.landform === 'continent' ? 'continent' : 'island';
  const total = width * height;
  const heightField = new Array<number>(total);
  const moisture = new Array<number>(total);
  const fertility = new Array<number>(total);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = cellIndex(x, y, width);
      const scale = Math.max(12, Math.min(width, height) / 2.8);
      let value = fractalNoise(numericSeed, x, y, scale);
      if (landform === 'island') {
        const nx = (x / Math.max(1, width - 1)) * 2 - 1;
        const ny = (y / Math.max(1, height - 1)) * 2 - 1;
        const edge = Math.pow(Math.max(Math.abs(nx), Math.abs(ny)), 2.2);
        value -= edge * 0.48;
      } else {
        value += (x / Math.max(1, width - 1) - 0.5) * 0.08;
      }
      heightField[index] = value;
      moisture[index] = fractalNoise(numericSeed + 31337, x, y, Math.max(9, scale * 0.7));
      fertility[index] = fractalNoise(numericSeed + 90121, x, y, Math.max(7, scale * 0.48));
    }
  }

  const sortedByHeight = Array.from({ length: total }, (_, index) => index)
    .sort((a, b) => heightField[a]! - heightField[b]! || a - b);
  const waterTarget = Math.round(total * (targets['deep-water'] + targets['shallow-water']) / 100);
  const isWater = new Array<boolean>(total).fill(false);
  sortedByHeight.slice(0, waterTarget).forEach((index) => { isWater[index] = true; });
  const assigned = new Array<TerrainId | null>(total).fill(null);
  const waterIndices = sortedByHeight.slice(0, waterTarget);
  const landIndices = sortedByHeight.slice(waterTarget);
  const coastDistance = new Array<number>(total).fill(99);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = cellIndex(x, y, width);
      if (isWater[index] && neighbours(x, y, width, height).some((point) => !isWater[cellIndex(point.x, point.y, width)])) {
        coastDistance[index] = 0;
      }
      if (!isWater[index] && neighbours(x, y, width, height).some((point) => isWater[cellIndex(point.x, point.y, width)])) {
        coastDistance[index] = 0;
      }
    }
  }
  for (let pass = 1; pass <= 4; pass += 1) {
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = cellIndex(x, y, width);
        const nearest = Math.min(...neighbours(x, y, width, height).map((point) => coastDistance[cellIndex(point.x, point.y, width)]!));
        coastDistance[index] = Math.min(coastDistance[index]!, nearest + 1);
      }
    }
  }

  const shallowDesired = Math.min(waterIndices.length, Math.round(total * targets['shallow-water'] / 100));
  const shoreWater = waterIndices.filter((index) => coastDistance[index] === 0);
  shoreWater.forEach((index) => { assigned[index] = 'shallow-water'; });
  takeByScore(waterIndices, Math.max(0, shallowDesired - shoreWater.length), (index) => 10 - coastDistance[index]! + moisture[index]!, assigned, 'shallow-water');
  waterIndices.forEach((index) => { assigned[index] ??= 'deep-water'; });

  const desired = (id: TerrainId) => Math.round(total * targets[id] / 100);
  const shoreLand = landIndices.filter((index) => coastDistance[index] === 0);
  shoreLand.forEach((index) => { assigned[index] = 'sand'; });
  takeByScore(landIndices, Math.max(0, desired('sand') - shoreLand.length), (index) => 12 - coastDistance[index]! - heightField[index]!, assigned, 'sand');
  takeByScore(landIndices, desired('rock'), (index) => heightField[index]! * 1.8 - moisture[index]! * 0.4, assigned, 'rock');
  takeByScore(landIndices, desired('mud'), (index) => moisture[index]! * 1.5 + (8 - coastDistance[index]!) * 0.15 - heightField[index]!, assigned, 'mud');
  takeByScore(landIndices, desired('earth'), (index) => 1.1 - moisture[index]! + heightField[index]! * 0.35, assigned, 'earth');
  takeByScore(landIndices, desired('meadow'), (index) => fertility[index]! + moisture[index]! * 0.4 - Math.abs(heightField[index]! - 0.48), assigned, 'meadow');
  landIndices.forEach((index) => { assigned[index] ??= 'grass'; });

  const landHeights = landIndices.map((index) => heightField[index]!);
  const minLand = Math.min(...landHeights, 0);
  const maxLand = Math.max(...landHeights, 1);
  const elevations = new Array<number>(total).fill(0);
  landIndices.forEach((index) => {
    const normalized = (heightField[index]! - minLand) / Math.max(0.0001, maxLand - minLand);
    elevations[index] = clamp(Math.floor(normalized * (1 + heightIntensity * 3.8)), 0, 3);
  });
  for (let pass = 0; pass < 5; pass += 1) {
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = cellIndex(x, y, width);
        if (isWater[index]) continue;
        const neighbourLevels = neighbours(x, y, width, height)
          .map((point) => cellIndex(point.x, point.y, width))
          .filter((neighbourIndex) => !isWater[neighbourIndex])
          .map((neighbourIndex) => elevations[neighbourIndex]!);
        if (neighbourLevels.length) {
          elevations[index] = Math.min(elevations[index]!, Math.min(...neighbourLevels) + 1);
        }
      }
    }
  }

  const cells: Cell[] = assigned.map((terrain, index) => ({
    terrain: terrain ?? 'grass',
    elevation: isWater[index] ? 0 : elevations[index]!,
    variant: Math.floor(random() * 4),
    path: null,
  }));

  return {
    schemaVersion: 1,
    id: '00000000-0000-4000-8000-000000000000',
    title: title.trim() || 'Untitled world',
    status: 'private',
    width,
    height,
    seed,
    stylePackVersion,
    revision: 0,
    generator: { seed, targets, heightIntensity, landform },
    cells,
    objects: [],
    spawnPoints: [],
    metadata: {},
  };
}
